import React, { FC, useMemo, useState } from 'react';
import ContentHeader from '../../components/Admin/ContentHeader';
import BranchQueueFilter from '../../components/Admin/BranchQueueFilter/BranchQueueFilter';
import AllItemsTable from '../../components/Admin/Tables/CategoriesAndServicesTable/AllItemsTable/AllItemsTable';
import SectionsTable from '../../components/Admin/Tables/CategoriesAndServicesTable/SectionsTable/SectionsTable';
import ServicesTable from '../../components/Admin/Tables/CategoriesAndServicesTable/ServicesTable/ServicesTable';
import { ModalSectionService } from '../../components/Admin/Modals/ModalSectionService/ModalSectionService';
import classes from './SectionServicePage.module.scss';
import { useGetAllCategoriesTopics } from '../../hooks/Admin/CategoriesAndServisesTable/useGetAllCategoriesTopics';
import { modalsSectionServiceDataType, SectionOrServiceForTable } from '../../types/Admin/CategoriesTopicsTypes';
import { SectionOrCategory, TableName, TableType } from '../../types/enums';
import { ModalDelete } from '../../components/Admin/Modals/ModalDelete/ModalDelete';
import { filterCategoriesServicesByBranchQueue } from '../../utils/Admin/CategoriesAndServicesTable/filterCategoriesServicesByBranchQueue';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useAdminQueueIdsFilter } from '../../hooks/Admin/useAdminQueueIdsFilter';

const tableName = 'Категории и услуги';

const tabs = [
  { data: TableType.All, tabText: TableName.All },
  { data: TableType.Sections, tabText: TableName.Sections },
  { data: TableType.Services, tabText: TableName.Services }
];

const tablesMap = {
  all: AllItemsTable,
  sections: SectionsTable,
  services: ServicesTable
};
const SectionServicesPage: FC = () => {
  const { canView, canEdit: canEditPermission } = useMyPermissions();
  const canCreate = canView("SERVICES_CREATE");
  const canEdit = canEditPermission("SERVICES_EDIT");
  const canDelete = canEditPermission("SERVICES_DELETE");
  const canChangeActivity = canEditPermission("SERVICES_ACTIVITY");
  const { queueIdsFilter } = useAdminQueueIdsFilter();

  const [activeTable, setActiveTable] = useState('all');
  const TableComponent = tablesMap[activeTable];
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [modalDeleteActive, setModalDeleteActive] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState('');
  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);

  const [activeRowData, setActiveRowData] = useState<SectionOrServiceForTable | null>(null);

  // Важно: при выборе очереди фильтр автоматически выбирает и филиал.
  // Но backend-параметр branchId для /categories-topics/view может "сужать" набор услуг иначе,
  // чем UI-фильтр по очередям (например, если связи "услуга-филиал" не заполнены, но связи "услуга-очередь" есть).
  // Поэтому branchId передаём только когда фильтруем именно по филиалу (и НЕ по очередям).
  const branchIdForView =
    filterQueueIds.length === 0 && filterBranchIds.length === 1
      ? filterBranchIds[0]
      : undefined;
  const { allCategoriesTopicsResp, isLoadingAllCategoriesTopics } = useGetAllCategoriesTopics(branchIdForView);

  const filteredTableData = useMemo(
    () => filterCategoriesServicesByBranchQueue(allCategoriesTopicsResp, filterBranchIds, filterQueueIds),
    [allCategoriesTopicsResp, filterBranchIds, filterQueueIds]
  );

  const handleActiveModal = (): void => {
    setModalActive(true);
    setActiveRowData(null);
  };

  const checkRadioType =
    activeRowData !== null
      ? activeTable === TableType.All
        ? activeRowData.radioType ?? ''
        : activeTable === TableType.Sections
          ? SectionOrCategory.Section
          : SectionOrCategory.Service
      : activeTable === TableType.Services
        ? SectionOrCategory.Service
        : SectionOrCategory.Section;

  const modalRowData = useMemo<modalsSectionServiceDataType>(() => {
    if (activeRowData) {
      return {
        ...activeRowData,
        radioType: checkRadioType,
      } as modalsSectionServiceDataType;
    }
    return {
      id: 0,
      nameRu: '',
      nameBe: '',
      nameEn: '',
      startFinishWork: '',
      radioType: checkRadioType,
    };
  }, [activeRowData, checkRadioType]);

  return (
    <div className={classes.container}>
      <ContentHeader
        name={tableName}
        handleClickTabs={setActiveTable}
        handleButtonClick={canCreate ? handleActiveModal : undefined}
        tabs={tabs}
        setModalMode={setModalMode}
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            queueIdsFilter={queueIdsFilter}
          />
        }
      />
      {modalActive && (
        <ModalSectionService
          isActive={modalActive}
          setActive={setModalActive}
          activeRowData={modalRowData}
          radioType={checkRadioType}
          modalMode={modalMode}
        />
      )}
      {modalDeleteActive && (
        <ModalDelete
          isActive={modalDeleteActive}
          setActive={setModalDeleteActive}
          activeRowData={activeRowData}
        />
      )}

      <TableComponent
        activeRowData={activeRowData}
        setActiveRowData={setActiveRowData}
        setModalActive={setModalActive}
        setModalDeleteActive={setModalDeleteActive}
        tableData={filteredTableData ?? allCategoriesTopicsResp}
        setModalMode={setModalMode}
        isLoadingData={isLoadingAllCategoriesTopics}
        canEdit={canEdit}
        canDelete={canDelete}
        canChangeActivity={canChangeActivity}
      />
    </div>
  );
};

export default SectionServicesPage;

